import React from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Download, Smartphone, Apple, Mail, BookOpen, ClipboardList, Trophy, Sparkles, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import MobileMockup from "@/components/MobileMockup";
import { useAuth } from "@/contexts/AuthContext";

const PLAY_STORE = "https://play.google.com/store/apps/details?id=app.emergent.neetpgprep7c9f4774b";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const copyUserId = async () => {
    try {
      await navigator.clipboard.writeText(user.user_id);
      toast.success("User ID copied to clipboard");
    } catch {
      toast.error("Could not copy. Please copy manually.");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="dashboard-home-btn">
              Home
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="dashboard-logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        {/* Welcome + ID */}
        <section className="lg:col-span-2 space-y-6">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-3xl p-8 relative overflow-hidden" data-testid="dashboard-welcome-card">
            <div className="absolute -right-10 -bottom-10 text-white/10 text-9xl font-black">+</div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" />
              <span className="text-xs font-bold uppercase tracking-[0.2em]">Future Doctor</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">
              Hi, Dr. {user.name} 👋
            </h1>
            <p className="opacity-90 mt-1">Let&apos;s continue your NEET PG preparation.</p>

            <div className="mt-6 bg-white/10 backdrop-blur rounded-2xl p-4 sm:p-5 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Your DearDr User ID</div>
                <div className="font-heading text-2xl sm:text-3xl font-extrabold tracking-[0.2em] mt-1" data-testid="dashboard-user-id">
                  {user.user_id}
                </div>
              </div>
              <Button onClick={copyUserId} variant="secondary" className="bg-white text-blue-700 hover:bg-slate-100" data-testid="dashboard-copy-userid">
                <Copy className="h-4 w-4 mr-2" /> Copy
              </Button>
            </div>
          </div>

          {/* Profile card */}
          <div className="bg-white border border-slate-200 rounded-3xl p-6" data-testid="dashboard-profile-card">
            <h2 className="font-heading text-xl font-bold text-slate-900">Your profile</h2>
            <dl className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-slate-500">Full name</dt>
                <dd className="font-semibold text-slate-900 mt-0.5">{user.name}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Email</dt>
                <dd className="font-semibold text-slate-900 mt-0.5 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" /> {user.email}
                </dd>
              </div>
              {user.phone && (
                <div>
                  <dt className="text-slate-500">Phone</dt>
                  <dd className="font-semibold text-slate-900 mt-0.5">{user.phone}</dd>
                </div>
              )}
              <div>
                <dt className="text-slate-500">Plan</dt>
                <dd className="font-semibold text-slate-900 mt-0.5">7-day Free Trial</dd>
              </div>
            </dl>
          </div>

          {/* Download CTAs */}
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8" data-testid="dashboard-download-card">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-blue-50 ring-1 ring-blue-600/10 flex items-center justify-center">
                <Smartphone className="h-5 w-5 text-blue-700" />
              </div>
              <h2 className="font-heading text-xl font-bold text-slate-900">Download the DearDr mobile app</h2>
            </div>
            <p className="text-sm text-slate-600 mt-3">
              Continue your prep on the go. Use the same User ID and password to log in on mobile.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href={PLAY_STORE} target="_blank" rel="noopener noreferrer" data-testid="dashboard-play-store"
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 h-12 rounded-xl text-sm font-semibold transition-colors">
                <Download className="h-4 w-4" /> Get on Google Play
              </a>
              <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-500 px-5 h-12 rounded-xl text-sm font-semibold cursor-not-allowed">
                <Apple className="h-4 w-4" /> App Store · Coming soon
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: BookOpen, label: "Start MCQ Practice", color: "bg-blue-50 text-blue-700" },
              { icon: ClipboardList, label: "Take a Mock Test", color: "bg-blue-50 text-blue-700" },
              { icon: Trophy, label: "Grand Test Ranks", color: "bg-amber-50 text-amber-700" },
            ].map((q) => {
              const I = q.icon;
              return (
                <button key={q.label} type="button"
                  onClick={() => toast.info("Available on the DearDr mobile app — download to continue.")}
                  className="text-left bg-white border border-slate-200 rounded-2xl p-5 lift"
                  data-testid={`dashboard-quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className={`h-10 w-10 rounded-xl ${q.color} flex items-center justify-center`}>
                    <I className="h-5 w-5" />
                  </div>
                  <div className="mt-3 font-semibold text-slate-900">{q.label}</div>
                  <div className="text-xs text-slate-500 mt-1">Continue inside the mobile app</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Side: mockup */}
        <aside className="hidden lg:flex justify-center">
          <MobileMockup />
        </aside>
      </main>
    </div>
  );
}
